/**
 * OPTIMIZED: Auto-selects color variants with minimal flicker
 * This creates the effect of showing each color variant as a separate product
 * Key improvements: immediate execution, better CSS hiding, reduced delays
 */

// This file is injected once per `product-list` section, so a page can include it
// several times. The whole script is wrapped in an IIFE (keeping its helpers out of
// the global scope, which previously caused "Identifier 'executionAttempts' has
// already been declared" SyntaxErrors) and guarded so it only initializes once.
(function () {
  if (window.__sippixColorVariantAutoSelectInit) return;
  window.__sippixColorVariantAutoSelectInit = true;

// Inject skeleton + fade CSS immediately
(function() {
  const style = document.createElement('style');
  style.textContent = `
    /* Skeleton loading state for variant products */
    .product-grid__item[data-auto-select-variant-id] .card-gallery,
    .resource-list__item[data-auto-select-variant-id] .card-gallery {
      position: relative;
    }

    /* Hide actual images initially */
    .product-grid__item[data-auto-select-variant-id] slideshow-component,
    .resource-list__item[data-auto-select-variant-id] slideshow-component,
    .product-grid__item[data-auto-select-variant-id] .product-card__media,
    .resource-list__item[data-auto-select-variant-id] .product-card__media {
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }

    /* Skeleton placeholder */
    .product-grid__item[data-auto-select-variant-id]:not(.variant-ready) .card-gallery::before,
    .resource-list__item[data-auto-select-variant-id]:not(.variant-ready) .card-gallery::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        rgb(var(--color-foreground-rgb) / 0.08) 0%,
        rgb(var(--color-foreground-rgb) / 0.12) 50%,
        rgb(var(--color-foreground-rgb) / 0.08) 100%
      );
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.5s ease-in-out infinite;
      border-radius: var(--gallery-aspect-ratio, 8px);
      z-index: 1;
      aspect-ratio: var(--gallery-aspect-ratio, 1);
    }

    /* Skeleton shimmer animation */
    @keyframes skeleton-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* Show images with fade when ready */
    .product-grid__item[data-auto-select-variant-id].variant-ready slideshow-component,
    .resource-list__item[data-auto-select-variant-id].variant-ready slideshow-component,
    .product-grid__item[data-auto-select-variant-id].variant-ready .product-card__media,
    .resource-list__item[data-auto-select-variant-id].variant-ready .product-card__media {
      opacity: 1;
      transition: opacity 0.4s ease-out;
    }

    /* Hide skeleton when ready */
    .product-grid__item[data-auto-select-variant-id].variant-ready .card-gallery::before,
    .resource-list__item[data-auto-select-variant-id].variant-ready .card-gallery::before {
      opacity: 0;
      transition: opacity 0.2s ease-out;
    }

    /* Subtle transition during variant switching */
    .product-grid__item[data-auto-select-variant-id].variant-updating slideshow-component,
    .resource-list__item[data-auto-select-variant-id].variant-updating slideshow-component,
    .product-grid__item[data-auto-select-variant-id].variant-updating .product-card__media,
    .resource-list__item[data-auto-select-variant-id].variant-updating .product-card__media {
      opacity: 0.6;
      transition: opacity 0.15s ease-out;
    }

    /* Ensure skeleton doesn't interfere with interactions */
    .card-gallery::before {
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
})();

const CARD_SELECTOR =
  '.product-grid__item[data-auto-select-variant-id], .resource-list__item[data-auto-select-variant-id]';

function autoSelectVariants(fromCache = false) {
  // Find all product grid items and resource list items that should auto-select a specific variant
  const productCards = document.querySelectorAll(CARD_SELECTOR);

  productCards.forEach(function (cardItem) {
    // Idempotent: a re-run only picks up cards that arrived since the last one.
    // Sections that render late (product recommendations) call back in here, and
    // re-driving a card that already settled would flash its image.
    if (cardItem.classList.contains('variant-ready') || cardItem.classList.contains('variant-updating')) {
      return;
    }

    const targetVariantId = cardItem.getAttribute('data-auto-select-variant-id');
    const colorOptionName = cardItem.getAttribute('data-color-option-name');
    const colorValue = cardItem.getAttribute('data-color-value');
    const productId = cardItem.getAttribute('data-product-id');
    const targetMediaId = cardItem.getAttribute('data-auto-select-media-id');

    if (!targetVariantId || !productId) {
      // No variant to select, mark as ready immediately
      cardItem.classList.add('variant-ready');
      return;
    }

    // Find the product card element within this list item
    const productCard = cardItem.querySelector('product-card');
    if (!productCard) {
      cardItem.classList.add('variant-ready');
      return;
    }

    // Immediate variant selection with minimal delay
    const selectVariant = () => {
      // Point the card's links at this colour before anything else. Cards without
      // a swatches block (product recommendations, for one) never reach the
      // branch below, so doing this only there left every colour tile of a
      // product linking to the product's default variant. See updateCardLinks.
      updateCardLinks(cardItem, targetVariantId);

      // Find the swatch input that matches this variant
      const swatchInputs = productCard.querySelectorAll('input[type="radio"][name*="-swatch"]');
      let targetInput = null;

      // Try to find the exact variant match
      for (const input of swatchInputs) {
        const inputVariantId = input.getAttribute('data-variant-id') ||
                              input.getAttribute('data-first-available-or-first-variant-id');

        if (inputVariantId === targetVariantId) {
          targetInput = input;
          break;
        }
      }

      // If we didn't find an exact match, try matching by color value
      if (!targetInput && colorValue) {
        for (const input of swatchInputs) {
          if (input.value === colorValue) {
            targetInput = input;
            break;
          }
        }
      }

      // Programmatically select this swatch
      if (targetInput) {
        // Always set checked state, even if already checked (important for bfcache)
        targetInput.checked = true;

        // Keep the card's links in step with the swatch, so quick-add fetches
        // this colour instead of the product default. See updateCardLinks.
        updateCardLinks(cardItem, targetInput.dataset.variantId || targetVariantId);

        // Function to mark variant as ready (with validation)
        const markReady = () => {
          // Verify the correct image is showing before marking as ready
          const slideshow = productCard.querySelector('slideshow-component');
          if (slideshow && targetInput) {
            const expectedImageId = targetInput.dataset.optionMediaId;
            const visibleSlide = slideshow.querySelector(`[slide-id="${expectedImageId}"]:not([hidden])`);

            if (expectedImageId && !visibleSlide) {
              // Image switch didn't work, try to force it
              console.log(`[Variant Script] Image switch failed for ${targetVariantId}, forcing update`);
              updateSlideshowForVariant(productCard, targetInput);

              // Give it another moment then mark ready
              setTimeout(() => {
                cardItem.classList.add('variant-ready');
                cardItem.classList.remove('variant-updating');
              }, 50);
            } else {
              cardItem.classList.add('variant-ready');
              cardItem.classList.remove('variant-updating');
            }
          } else {
            cardItem.classList.add('variant-ready');
            cardItem.classList.remove('variant-updating');
          }
        };

        // Listen for variant update event to know when image has actually changed
        const handleVariantUpdate = (event) => {
          // Use requestAnimationFrame for smooth transition
          requestAnimationFrame(() => {
            markReady();
            productCard.removeEventListener('theme:variant:update', handleVariantUpdate);
          });
        };

        // Listen for the variant update event
        productCard.addEventListener('theme:variant:update', handleVariantUpdate, { once: true });

        // Add updating class for transition
        cardItem.classList.add('variant-updating');

        // Dispatch events to trigger variant updates
        const changeEvent = new Event('change', { bubbles: true });
        targetInput.dispatchEvent(changeEvent);

        const inputEvent = new Event('input', { bubbles: true });
        targetInput.dispatchEvent(inputEvent);

        // When restoring from cache, also directly update the slideshow
        if (fromCache) {
          updateSlideshowForVariant(productCard, targetInput);
        }

        // Fallback timeout in case event doesn't fire (reduced from 500ms to 200ms)
        setTimeout(markReady, 200);
      } else {
        // No swatch input to drive. Product cards only render swatch radios when
        // the card has a swatches block, so on grids built from just gallery +
        // title + price (product recommendations, for one) every colour tile was
        // left showing the product's first image. The section hands us the
        // variant's media id for exactly this case — switch the slide directly.
        if (targetMediaId) {
          showMediaInCard(productCard, targetMediaId);
        }
        cardItem.classList.add('variant-ready');
      }
    };

    // Execute immediately if DOM is ready, otherwise with minimal delay
    if (document.readyState === 'loading') {
      // DOM still loading, use small delay
      setTimeout(selectVariant, fromCache ? 10 : 20);
    } else {
      // DOM ready, execute immediately
      selectVariant();
    }
  });
}

/**
 * Points every product link inside a card at the variant that card is showing.
 *
 * snippets/product-card.liquid builds its hrefs from
 * `product.selected_or_first_available_variant`, which is identical for every
 * colour-expanded card of the same product. So a pink card and a black card
 * both link to `?variant=<black>`. Selecting the swatch above fixes the radio
 * but leaves those links stale, which breaks two things:
 *
 *   1. Clicking a non-default colour opens the PDP on the wrong colour.
 *   2. assets/quick-add.js returns early when the href already carries a
 *      `variant` param, so it never consults the card's own selection. The
 *      modal fetches the default variant's markup, paints it, and only then
 *      does #syncVariantSelection check the right radio and fire `change` —
 *      the visible flash of the wrong colour when the modal opens.
 *
 * Rewriting the hrefs fixes both at the source rather than papering over the
 * flash with a spinner.
 *
 * @param {Element} cardItem - The grid/list item wrapping the card
 * @param {string} variantId - The variant this card represents
 */
function updateCardLinks(cardItem, variantId) {
  if (!variantId) return;

  for (const link of cardItem.querySelectorAll('a[href*="/products/"]')) {
    try {
      const url = new URL(link.href, window.location.origin);
      if (url.searchParams.get('variant') === String(variantId)) continue;
      url.searchParams.set('variant', String(variantId));
      link.href = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      // A malformed href is not worth throwing over; leave it as authored.
    }
  }
}

/**
 * Directly updates the slideshow to show the correct variant image
 * This is needed when restoring from bfcache as the component state might be stale
 */
function updateSlideshowForVariant(productCard, selectedInput) {
  if (!selectedInput) return;
  showMediaInCard(productCard, selectedInput.dataset.optionMediaId);
}

/**
 * Shows one specific media in a card's gallery and hides the other variant
 * images. The media id doubles as the slide id in card-gallery.liquid.
 */
function showMediaInCard(productCard, selectedImageId) {
  if (!productCard || !selectedImageId) return;

  // Find the slideshow component
  const slideshow = productCard.querySelector('slideshow-component');
  if (!slideshow) return;

  // Find all slides
  const slides = slideshow.querySelectorAll('[slide-id]');
  if (!slides.length) return;

  // Nothing to do if this card doesn't carry the requested media.
  const targetSlide = slideshow.querySelector(`[slide-id="${selectedImageId}"]`);
  if (!targetSlide) return;

  // Show/hide slides based on variant
  slides.forEach(slide => {
    if (slide.getAttribute('variant-image') != null) {
      slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
    }
  });

  // Select the correct slide
  if (slideshow.select) {
    slideshow.select({ id: selectedImageId }, undefined, { animate: false });
  }
}

// ROBUST EXECUTION STRATEGY - handle hard refresh timing issues
let executionAttempts = 0;
let hasSuccessfulExecution = false;

function attemptExecution(context = 'unknown') {
  executionAttempts++;
  console.log(`[Variant Script] Attempt ${executionAttempts} (${context})`);

  // Check if we have elements to work with
  const targetElements = document.querySelectorAll(CARD_SELECTOR);

  if (targetElements.length === 0) {
    console.log(`[Variant Script] No target elements found, will retry`);
    return false;
  }

  autoSelectVariants(context === 'cache');
  hasSuccessfulExecution = true;
  console.log(`[Variant Script] Execution completed successfully`);
  return true;
}

// Strategy 1: Immediate execution (fastest, but might be too early)
if (document.readyState !== 'loading') {
  attemptExecution('immediate');
}

// Strategy 2: DOMContentLoaded (most reliable)
document.addEventListener('DOMContentLoaded', function() {
  if (!hasSuccessfulExecution) {
    attemptExecution('DOMContentLoaded');
  }
});

// Strategy 3: Retry mechanism for hard refreshes
function retryExecution() {
  if (!hasSuccessfulExecution && executionAttempts < 5) {
    setTimeout(() => {
      if (attemptExecution('retry')) {
        hasSuccessfulExecution = true;
      } else {
        retryExecution(); // Keep trying
      }
    }, 100 * executionAttempts); // Exponential backoff
  }
}

// Strategy 4: Start retry mechanism after a short delay
setTimeout(() => {
  if (!hasSuccessfulExecution) {
    retryExecution();
  }
}, 50);

// Strategy 5: Window load as final fallback
window.addEventListener('load', function() {
  if (!hasSuccessfulExecution) {
    console.log('[Variant Script] Final fallback execution');
    attemptExecution('window-load');
  }
});

// Strategy 6: cards that arrive after the page has loaded.
// Product recommendations render through a second request, so every attempt
// above ran against a DOM that had none of those cards in it. Nothing picked
// them up afterwards, which left them wearing the skeleton styles at the top of
// this file forever: shimmering grey blocks with opacity:0 galleries. Watch for
// them instead of guessing at timing.
let pendingScan = null;

function scheduleScan(context) {
  if (pendingScan) return;
  pendingScan = setTimeout(() => {
    pendingScan = null;
    if (document.querySelector(CARD_SELECTOR)) {
      attemptExecution(context);
      sweepStragglers();
    }
  }, 50);
}

/**
 * Last resort: reveal anything still marked unready. A card that never resolves
 * should fall back to the image the server rendered, never to a grey box.
 */
let sweepTimer = null;

function sweepStragglers() {
  clearTimeout(sweepTimer);
  sweepTimer = setTimeout(() => {
    document.querySelectorAll(CARD_SELECTOR).forEach((cardItem) => {
      if (!cardItem.classList.contains('variant-ready')) {
        cardItem.classList.add('variant-ready');
        cardItem.classList.remove('variant-updating');
      }
    });
  }, 3000);
}

if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR)) {
          scheduleScan('mutation');
          return;
        }
      }
    }
  });

  // Only childList, so the class and hidden attributes this script writes can't
  // feed the observer back into itself.
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

sweepStragglers();

// Shopify re-renders sections in place for filtering and pagination.
document.addEventListener('shopify:section:load', () => scheduleScan('section-load'));

// Run when page is restored from back/forward cache (bfcache)
window.addEventListener('pageshow', function(event) {
  if (event.persisted) {
    // Page was restored from bfcache, reset and re-run
    console.log('[Variant Script] Page restored from cache - resetting');
    resetAndRerun('pageshow');
  }
});

// Also listen for popstate (back/forward button)
window.addEventListener('popstate', function() {
  console.log('[Variant Script] Popstate detected - resetting');
  setTimeout(() => resetAndRerun('popstate'), 10);
});

// Listen for pagereveal event (View Transitions API)
window.addEventListener('pagereveal', function() {
  console.log('[Variant Script] Page reveal detected - resetting');
  setTimeout(() => resetAndRerun('pagereveal'), 10);
});

// Helper function to reset state and rerun
function resetAndRerun(context) {
  // Reset execution tracking
  hasSuccessfulExecution = false;
  executionAttempts = 0;

  // Reset all ready/updating states
  document.querySelectorAll('.variant-ready, .variant-updating').forEach(el => {
    el.classList.remove('variant-ready', 'variant-updating');
  });

  // Attempt execution
  attemptExecution(context);
}
})();